import React from 'react'
import { Box } from '@chakra-ui/core'
import { PushToastCard } from 'components/Notifications/PushToastCard'
import { INotification, NavigateFn } from 'services/notifications/types'
import { handleNotificationClick } from 'services/notifications/handleNotificationClick'

export interface IToastEntry {
  id: string
  notification: INotification
  exiting?: boolean
}

interface IProps {
  toasts: IToastEntry[]
  onDismiss: (chronoKey: string) => void
  onMarkSeen: (notification: INotification) => void
  navigate: NavigateFn
  isRtl?: boolean
  dispatch?: (action: any) => any
}

// Cards behind the front one sit slightly scaled down and lifted, fanned from the bottom-right
// corner — a depth cue, not a real stack of DOM layers.
const DEPTH_TRANSFORMS = ['scale(1)', 'scale(0.97) translateY(-1px)', 'scale(0.94) translateY(-2px)']

// Bottom-right, newest on top — see push-sw.js's respondToNotification and
// usePushNotificationMessages for how entries arrive. Product now allows the stack to grow without
// evicting older focused-tab toasts; cards beyond the designed third depth keep the deepest visual.
export const NotificationToastStack: React.FC<IProps> = ({
  toasts,
  onDismiss,
  onMarkSeen,
  navigate,
  isRtl = false,
  dispatch,
}) => {
  if (!toasts.length) return null

  return (
    <Box
      position="fixed"
      bottom="20px"
      left={isRtl ? '20px' : 'auto'}
      right={isRtl ? 'auto' : '20px'}
      zIndex={3000}
      display="flex"
      flexDirection="column"
      style={{ gap: '10px' }}
    >
      {toasts.map(({ id, notification, exiting }, index) => (
        <PushToastCard
          key={id}
          notification={notification}
          isExiting={!!exiting}
          isFront={index === 0}
          depthTransform={DEPTH_TRANSFORMS[index] ?? DEPTH_TRANSFORMS[DEPTH_TRANSFORMS.length - 1]}
          isRtl={isRtl}
          onDismiss={() => onDismiss(id)}
          onClick={() => {
            onDismiss(id)
            handleNotificationClick(notification, navigate, { markSeen: onMarkSeen, dispatch }).catch((err) =>
              console.warn('[Notifications] click handling failed:', err)
            )
          }}
        />
      ))}
    </Box>
  )
}
